import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const TutoringAgentFileUpload = ({ 
  documentType, 
  title, 
  onFileSelected,
  onFileRemoved,
  selectedFile,
  hasError 
}) => {
  const [uploadError, setUploadError] = useState('');

  const handleFile = useCallback((file) => {
    // Validate file size (12MB limit)
    const maxSizeInBytes = 12 * 1024 * 1024; // 12MB
    if (file.size > maxSizeInBytes) {
      const errorMsg = `File size exceeds 12MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`;
      setUploadError(errorMsg);
      if (onFileRemoved) {
        onFileRemoved(documentType);
      }
      return;
    }

    // Validate file type (common document/image types)
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = "Please upload a valid document (PDF, Word, Excel, Image, or Text file).";
      setUploadError(errorMsg);
      if (onFileRemoved) {
        onFileRemoved(documentType);
      }
      return;
    }

    setUploadError('');
    if (onFileSelected) {
      onFileSelected(documentType, file);
    }
  }, [documentType, onFileSelected, onFileRemoved]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]; // Only handle the first file
      handleFile(file);
    }
  }, [handleFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt']
    },
    multiple: false
  });

  const hasFile = !!selectedFile;
  
  return (
    <div className="col-12">
      <div className={`card h-100 ${hasError ? 'border-danger' : hasFile ? 'border-success' : 'border-secondary'}`}>
        <div 
          {...getRootProps()} 
          className={`card-body d-flex flex-column ${isDragActive ? 'drag-active' : ''}`}
          style={{ 
            cursor: 'pointer',
            backgroundColor: hasFile ? '#f8f9fa' : 'white',
            minHeight: '150px'
          }}
        >
          <input {...getInputProps()} />
          <div className="text-center flex-grow-1 d-flex flex-column justify-content-center">
            {hasFile ? (
              <React.Fragment>
                <div className="mb-3">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="fw-bold mb-2">{title}</div>
                <div className="text-success small mb-2">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  {selectedFile.name}
                </div>
                <div className="text-muted small">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </div>
                <div className="text-muted small mt-2">
                  Click or drag to replace
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div className="mb-3">
                  <i className={`bi ${hasError ? 'bi-exclamation-triangle-fill text-danger' : 'bi-cloud-upload text-secondary'}`} style={{ fontSize: '2rem' }}></i>
                </div>
                <div className="fw-bold mb-2">{title}</div>
                <div className="text-muted small">
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop a file here, or click to browse'}
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  PDF, Word, Excel, Images (max 12MB)
                </div>
              </React.Fragment>
            )}
          </div>

          {uploadError && (
            <div className="text-danger small mt-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {uploadError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutoringAgentFileUpload;
